import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import Confetti from "react-confetti";
import { useWindowSize } from "@/hooks/useWindowSize";

import ParticleBackground from "@/components/ParticleBackground";
import GameBoard from "@/components/GameBoard";
import SOSBoard from "@/components/SOSBoard";
import GameSettings from "@/components/GameSettings";
import ScorePanel from "@/components/ScorePanel";
import SOSScorePanel from "@/components/SOSScorePanel";
import MusicControls from "@/components/MusicControls";
import WinnerAnnouncement from "@/components/WinnerAnnouncement";
import GameControls from "@/components/GameControls";
import Leaderboard from "@/components/Leaderboard";
import NameInputDialog from "@/components/NameInputDialog";
import PlayerNamesDialog from "@/components/PlayerNamesDialog";
import BadgeProfile from "@/components/BadgeProfile";
import FullscreenButton from "@/components/FullscreenButton";
import VoiceToggleButton from "@/components/VoiceToggleButton";
import { useDevice } from "@/hooks/useDevice";
import { recordGamePlayed, recordGameWon, getActivePlayer, setActivePlayer } from "@/utils/playerProfile";
import { SIMPLE_GAMES, SETTINGS_GAMES, OPEN_HANDLER_MAP } from "@/config/gameRegistry";

import { initSpeech } from "@/utils/soundEffects";
import { useBoardGame } from "@/hooks/useBoardGame";
import { useEducationalGames } from "@/hooks/useEducationalGames";

const BACKGROUND_IMAGE = "https://customer-assets.emergentagent.com/job_tic-tac-ai-8/artifacts/gkexwtjb_Golden_29.webp";

const TOAST_STYLE = {
  style: { background: 'rgba(26, 18, 5, 0.9)', border: '1px solid rgba(255, 215, 0, 0.3)', color: '#FFD700' },
};

function GameWrapper({ children }) {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="fixed inset-0 z-[-20] bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${BACKGROUND_IMAGE})`, opacity: 0.9 }} />
      <div className="fixed inset-0 z-[-15] bg-black/50" />
      <ParticleBackground />
      {children}
      <VoiceToggleButton />
      <FullscreenButton />
      <MusicControls />
      <Toaster position="top-center" toastOptions={TOAST_STYLE} />
    </div>
  );
}

// Wrapper for all educational/simple games (consistent layout)
function EduGameView({ children, centered = false }) {
  return (
    <GameWrapper>
      <div className={`relative z-10 min-h-screen flex flex-col items-center ${centered ? 'justify-center p-4' : 'pt-3 pb-4 px-2 sm:px-4'}`}>{children}</div>
    </GameWrapper>
  );
}

function App() {
  const bg = useBoardGame();
  const eg = useEducationalGames();
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showBadgeProfile, setShowBadgeProfile] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const { width: winW, height: winH } = useWindowSize();
  useDevice(); // activates body[data-device], body[data-orientation], body[data-touch] for CSS hooks

  useEffect(() => { initSpeech(); }, []);

  // Auto-set active player on first board game with a player name
  useEffect(() => {
    if (!getActivePlayer() && bg.playerXName && bg.playerXName !== "X") {
      setActivePlayer(bg.playerXName);
    }
  }, [bg.playerXName]);

  // Track game played for board games when started
  useEffect(() => {
    if (bg.gameStarted && bg.mode) {
      recordGamePlayed(bg.mode);
    }
  }, [bg.gameStarted, bg.mode]);

  // Track win when winner is decided for board games
  useEffect(() => {
    if (!bg.winner || bg.winner === "draw" || !bg.mode) return;
    // For PvP, winner == X or O or player name; record win regardless
    recordGameWon(bg.mode);
  }, [bg.winner, bg.mode]);

  // Register Service Worker
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js').catch(() => {});
    }
  }, []);

  // PWA Install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowInstallBanner(false);
  }, [deferredPrompt]);

  // Educational game screens
  // Settings games (registry-driven dispatch with shared dispatcher for settings + game phases)
  const activeSettings = SETTINGS_GAMES.find(g => eg[g.showGameKey] || eg[g.showSettingsKey]);
  if (activeSettings) {
    const inSettings = !eg[activeSettings.showGameKey];
    return (<EduGameView centered={inSettings}>{inSettings ? activeSettings.renderSettings(eg) : activeSettings.renderGame(eg)}</EduGameView>);
  }

  // Simple educational games (registry-driven dispatch)
  const activeSimple = SIMPLE_GAMES.find(g => eg[`show${g.name}Game`]);
  if (activeSimple) {
    const Comp = activeSimple.Component;
    return (<EduGameView><Comp onBackToMenu={eg[`handleBackFrom${activeSimple.name}`]} /></EduGameView>);
  }

  // Main menu or board game
  return (
    <GameWrapper>
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-4">
        {!bg.gameStarted ? (
          <div className="w-full max-w-6xl">
            {/* PWA Install Banner */}
            <AnimatePresence>
              {showInstallBanner && (
                <motion.div
                  initial={{ y: -40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -40, opacity: 0 }}
                  className="glass-card-strong rounded-2xl p-3 mb-4 flex items-center justify-between gap-3 border border-gold/30"
                  data-testid="pwa-install-banner"
                >
                  <div className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-gold flex-shrink-0" />
                    <p className="font-fredoka text-sm text-gold-light">
                      <span className="font-bold text-gold">Install ALBA GAME</span> di HP untuk main offline!
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button onClick={handleInstall} size="sm" className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-fredoka font-bold rounded-xl h-8 px-3 text-xs" data-testid="pwa-install-btn">
                      Install
                    </Button>
                    <Button onClick={() => setShowInstallBanner(false)} variant="ghost" size="sm" className="text-gold-light/50 hover:text-gold h-8 px-2 text-xs font-fredoka">
                      Nanti
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* Title */}
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
              className="text-center mb-6"
            >
              <motion.h1
                className="text-5xl sm:text-6xl lg:text-7xl font-black mb-2 drop-shadow-lg"
                style={{
                  fontFamily: "'Fredoka', 'Poppins', sans-serif",
                  background: 'linear-gradient(135deg, #FFD700, #FF6B35, #FFD700, #FFA500, #FFD700)',
                  backgroundSize: '300% 300%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))',
                }}
                animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                data-testid="main-title"
              >
                ALBA GAME
              </motion.h1>
              <motion.p
                className="text-gold-light text-base font-bold"
                style={{ fontFamily: "'Fredoka', sans-serif" }}
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {bg.mode === "classic" && "X O X - 3 berturut menang!"}
                {bg.mode === "gomoku" && "Gomoku - 5 berturut menang!"}
                {bg.mode === "sos" && "SOS - Bentuk pola S-O-S!"}
              </motion.p>
            </motion.div>

            <GameSettings
              mode={bg.mode}
              setMode={bg.setMode}
              playerType={bg.playerType}
              setPlayerType={bg.setPlayerType}
              aiDifficulty={bg.aiDifficulty}
              setAiDifficulty={bg.setAiDifficulty}
              boardSize={bg.boardSize}
              setBoardSize={bg.setBoardSize}
              onStartGame={bg.handleStartGame}
              hasSavedGame={bg.hasSavedGame}
              onContinueGame={bg.loadSavedGame}
              onResetScores={bg.resetScores}
              onOpenGame={(id) => { const k = OPEN_HANDLER_MAP[id]; if (k && eg[k]) eg[k](); }}
              sosPlayerCount={bg.sosPlayerCount}
              setSosPlayerCount={bg.setSosPlayerCount}
            />

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-3 text-center"
            >
              <Button
                onClick={() => setShowLeaderboard(true)}
                variant="ghost"
                data-testid="leaderboard-btn"
                className="text-white hover:bg-white/10 font-fredoka"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Lihat Leaderboard
              </Button>
              <Button
                onClick={() => setShowBadgeProfile(true)}
                variant="ghost"
                data-testid="badge-profile-btn"
                className="text-white hover:bg-white/10 font-fredoka ml-2"
              >
                🎖️ Profil Badge
              </Button>
            </motion.div>
          </div>
        ) : (
          <div className="w-full max-w-7xl flex flex-col items-center px-1 sm:px-2">
            {bg.sosConfetti && (
              <>
                <Confetti width={winW} height={winH} recycle={false} numberOfPieces={300} colors={["#00EAFF","#FF55BB","#FFD700","#4ade80","#F97316","#A855F7"]} gravity={0.2} initialVelocityY={25} />
                <Confetti width={winW} height={winH} recycle={false} numberOfPieces={100} colors={["#FFD700","#FFA500"]} gravity={0.15} initialVelocityX={15} confettiSource={{ x: 0, y: winH * 0.5, w: 10, h: 10 }} />
              </>
            )}
            {bg.mode === "sos" ? (
              <SOSScorePanel
                scores={bg.sosScores}
                currentPlayer={bg.sosCurrentPlayer}
                playerNames={bg.sosPlayerNames}
                playerCount={bg.sosPlayerCount}
              />
            ) : (
              <ScorePanel
                xWins={bg.xWins}
                oWins={bg.oWins}
                draws={bg.draws}
                currentPlayer={bg.currentPlayer}
                playerXName={bg.playerType === "pvp" ? bg.playerXName : "X"}
                playerOName={bg.playerType === "pvp" ? bg.playerOName : (bg.playerType === "ai" ? "AI" : "O")}
              />
            )}

            <div className="mb-4 overflow-auto max-w-full">
              {bg.mode === "sos" ? (
                <SOSBoard
                  board={bg.board}
                  size={bg.boardSize}
                  onCellClick={bg.handleSOSCellClick}
                  sosPatterns={bg.sosPatterns}
                  disabled={!!bg.winner || bg.isAIThinking}
                  currentPlayer={bg.sosCurrentPlayer}
                  playerNames={bg.sosPlayerNames}
                  playerCount={bg.sosPlayerCount}
                />
              ) : (
                <GameBoard
                  board={bg.board}
                  size={bg.boardSize}
                  onCellClick={bg.handleCellClick}
                  winningCells={bg.winningCells}
                  disabled={!!bg.winner || bg.isAIThinking}
                />
              )}
            </div>

            {bg.isAIThinking && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-4 font-fredoka text-orange flex items-center gap-2"
              >
                <div className="w-4 h-4 border-2 border-orange border-t-transparent rounded-full animate-spin" />
                AI sedang berpikir...
              </motion.div>
            )}

            <GameControls
              onUndo={bg.handleUndo}
              onReset={bg.handleReset}
              onBackToMenu={bg.handleBackToMenu}
              onShowLeaderboard={() => setShowLeaderboard(true)}
              onSaveGame={bg.saveGame}
              canUndo={bg.moveHistory.length > 0 && !bg.winner && !bg.isAIThinking}
              currentPlayer={bg.currentPlayer}
              playerType={bg.playerType}
              mode={bg.mode}
              boardSize={bg.boardSize}
              playerXName={bg.playerXName}
              playerOName={bg.playerOName}
              soundEnabled={bg.soundEnabled}
              onToggleSound={() => bg.setSoundEnabled(!bg.soundEnabled)}
            />
          </div>
        )}
      </div>

      <WinnerAnnouncement
        winner={bg.winner}
        onPlayAgain={bg.handlePlayAgain}
        onBackToMenu={bg.handleBackToMenu}
        isVisible={bg.showWinnerModal}
        playerXName={bg.playerXName}
        playerOName={bg.playerOName}
        playerType={bg.playerType}
        mode={bg.mode}
        sosScores={bg.sosScores}
        sosPlayerNames={bg.sosPlayerNames}
        sosPlayerCount={bg.sosPlayerCount}
        boardSize={bg.boardSize}
        moveCount={bg.moveHistory?.length || 0}
        gameStartTime={bg.gameStartTime}
      />

      <Leaderboard isOpen={showLeaderboard} onClose={() => setShowLeaderboard(false)} />
      <BadgeProfile isOpen={showBadgeProfile} onClose={() => setShowBadgeProfile(false)} />
      <NameInputDialog isOpen={bg.showNameInput} onSubmit={bg.handleSaveScore} onClose={() => {}} />
      <PlayerNamesDialog isOpen={bg.showPlayerNamesDialog} onSubmit={bg.handlePlayerNamesSubmit} onClose={() => {}} playerCount={bg.mode === "sos" ? bg.sosPlayerCount : 2} />
    </GameWrapper>
  );
}

export default App;
